import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ProfileAvatar from "@/components/ProfileAvatar";
import {
  LayoutGrid, Heart, Inbox, Sparkles, Truck, CreditCard, UserCircle, Package,
  Sparkle, ChevronLeft, ChevronRight, LogOut, Archive, Users, Store, Rss, Building2, X, Crown, Grid3x3, Flower2, Vault, ShieldCheck, FileText,
} from "lucide-react";

const items = [
  { to: "/dashboard", label: "The Gallery", icon: LayoutGrid, testId: "nav-dashboard" },
  { to: "/boutique", label: "My Boutique", icon: Store, testId: "nav-boutique" },
  { to: "/feed", label: "Vibe Feed", icon: Rss, testId: "nav-feed" },
  { to: "/loved-ones", label: "Loved Ones", icon: Heart, testId: "nav-loved-ones" },
  { to: "/circles", label: "Gifted Circle", icon: Users, testId: "nav-circles" },
  { to: "/captain", label: "Club Captain", icon: Building2, testId: "nav-captain" },
  { to: "/drafts", label: "Curations", icon: Inbox, testId: "nav-drafts" },
  { to: "/cards", label: "Design Studio", icon: Sparkles, testId: "nav-cards" },
  { to: "/gift-wall", label: "Gift Wall", icon: Grid3x3, testId: "nav-gift-wall" },
  { to: "/arrangements", label: "Arrangements", icon: Flower2, testId: "nav-arrangements" },
  { to: "/vault", label: "The Vault", icon: Archive, testId: "nav-vault" },
  { to: "/deliveries", label: "Sent with Love", icon: Truck, testId: "nav-deliveries" },
  { to: "/dashboard#wallet", label: "Maison Wallet", icon: Vault, testId: "nav-wallet", gold: true },
  { to: "/subscription", label: "Your Gifted Tier", icon: CreditCard, testId: "nav-subscription" },
  { to: "/membership", label: "Membership", icon: Crown, testId: "nav-membership" },
  { to: "/profile", label: "My Profile", icon: UserCircle, testId: "nav-profile" },
];

const ADMIN_ITEM = { to: "/admin/fulfillment", label: "Fulfillment View", icon: Package, testId: "nav-fulfillment" };

/**
 * Sidebar
 *  - variant="desktop": fixed vertical rail, visible only on lg+ (>=1024px). Collapsible.
 *  - variant="mobile": full nav list for the slide-in drawer (always expanded, scrollable,
 *    links auto-close the drawer via onNavigate; X close via onClose).
 */
const Sidebar = ({ onOpenSettings, collapsed, setCollapsed, variant = "desktop", onNavigate, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = variant === "mobile";
  const isCollapsed = !isMobile && collapsed;
  const navList = user?.is_root_admin ? [...items, ADMIN_ITEM] : items;

  const asideBase = isMobile
    ? "flex flex-col h-full w-72 surface"
    : `hidden lg:flex fixed left-0 top-0 h-full z-30 flex-col surface border-r border-border/40 transition-all duration-300 ${collapsed ? "w-[76px]" : "w-64"}`;

  return (
    <aside data-testid={isMobile ? "mobile-sidebar" : "sidebar"} className={asideBase} style={{ borderRadius: 0 }}>
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 h-20 shrink-0">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shrink-0 glow-purple">
          <Sparkle className="w-5 h-5 text-white" />
        </div>
        {!isCollapsed && <span data-testid={isMobile ? "drawer-brand" : "brand-logo"} className="gifted-brand text-2xl">GIFTED</span>}
        {isMobile && (
          <button data-testid="drawer-close" onClick={onClose}
            className="ml-auto p-2 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav — vertically scrollable */}
      <nav className="flex-1 overflow-y-auto px-3 space-y-1.5 mt-2">
        {navList.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testId}
            title={isCollapsed ? it.label : undefined}
            onClick={() => onNavigate && onNavigate()}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 rounded-2xl text-sm transition-all ${
                isActive
                  ? "bg-primary/20 text-foreground border border-primary/30"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent"
              } ${isCollapsed ? "justify-center" : ""}`
            }
          >
            <it.icon className={`w-5 h-5 shrink-0 ${it.gold ? "gold-text" : ""}`} />
            {!isCollapsed && <span className="truncate">{it.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Account */}
      <div className="px-3 pb-4 space-y-1.5 shrink-0">
        <button
          data-testid={isMobile ? "drawer-open-settings" : "open-settings"}
          onClick={() => { onOpenSettings && onOpenSettings(); if (onNavigate) onNavigate(); }}
          title={isCollapsed ? "Account" : undefined}
          className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all ${isCollapsed ? "justify-center" : ""}`}
        >
          <ProfileAvatar name={user?.name} sizeClass="w-8 h-8" textClass="text-xs font-medium" isRootAdmin={!!user?.is_root_admin} />
          {!isCollapsed && (
            <div className="flex flex-col items-start leading-tight min-w-0">
              <span className="text-sm text-foreground truncate max-w-[150px]">{user?.name}</span>
              <span className="text-[11px] truncate max-w-[150px]">{user?.email}</span>
            </div>
          )}
        </button>

        {!isMobile && (
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="sidebar-toggle"
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all ${collapsed ? "justify-center" : ""}`}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /> <span>Collapse</span></>}
          </button>
        )}

        {/* Legal Footing — Privacy Policy & Terms of Service must be reachable within two
            taps of the home screen (Apple/Google requirement). Placed directly in the
            mobile drawer so they're always one tap away from the hamburger menu. */}
        {isMobile && (
          <div className="flex items-center justify-center gap-4 pt-2 border-t border-border/40" data-testid="drawer-legal-links">
            <Link to="/privacy" data-testid="drawer-privacy-link" onClick={() => onNavigate && onNavigate()}
              className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Privacy Policy
            </Link>
            <span className="text-border">·</span>
            <Link to="/terms" data-testid="drawer-terms-link" onClick={() => onNavigate && onNavigate()}
              className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
              <FileText className="w-3 h-3" /> Terms of Service
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
};

export { items as navItems };
export default Sidebar;
